import { ODOO_BASE_URL } from '../../odooConfig';

// In local development, route attendance requests through the React dev server proxy
// to avoid browser CORS issues.
const isLocalDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const BASE_URL = isLocalDevelopment ? '' : ODOO_BASE_URL;

// Log which URL is being used for debugging
console.log('Student Attendance API Base URL:', BASE_URL || `(proxy → see setupProxy / _odooBaseUrlProduction)`);

/**
 * Attendance Management System API Client
 * Handles all API requests using JSON-RPC 2.0 protocol with Bearer Token authentication
 */
class AttendanceAPI {
  constructor(baseURL = BASE_URL) {
    this.baseURL = baseURL;
    this.defaultFacultyId = null;
  }

  setDefaultFacultyId(facultyId) {
    if (facultyId === null || facultyId === undefined || facultyId === '') {
      this.defaultFacultyId = null;
      return;
    }
    const parsed = Number(facultyId);
    this.defaultFacultyId = Number.isFinite(parsed) ? parsed : facultyId;
  }

  withDefaultFacultyScope(params = {}) {
    if (!params || typeof params !== 'object') return params;
    if (!this.defaultFacultyId) return params;
    if ('faculty_id' in params || 'faculty_ids' in params) return params;
    return { ...params, faculty_id: this.defaultFacultyId };
  }

  /**
   * Make a JSON-RPC 2.0 request
   * @param {string} endpoint - API endpoint
   * @param {object} params - Request parameters
   * @param {string} token - Bearer token for authentication
   * @returns {Promise<object>} API response
   */
  async request(endpoint, params = {}, token = null) {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token is provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestBody = {
      jsonrpc: '2.0',
      method: 'call',
      params: params
    };

    try {
      console.log(`API Request to: ${this.baseURL}${endpoint}`, { params, hasToken: !!token });
      
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      console.log(`API Response status: ${response.status}`, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`API request failed: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('API Response data:', data);
      console.log('API Response result:', data.result); // Debug log
      console.log('API Response error:', data.error); // Debug log

      // Handle JSON-RPC 2.0 error response
      if (data.error) {
        console.error('JSON-RPC Error detected:', data.error);
        const errorMessage = data.error.message || 'API request failed';
        
        // Special handling for mark attendance endpoint - check if result also has success
        // This handles cases where backend saves but returns error in response
        const isMarkEndpoint = endpoint.includes('/mark') || endpoint.includes('/session/mark');
        if (isMarkEndpoint && data.result) {
          console.warn('⚠️ Mark attendance endpoint returned error but also has result data');
          console.log('Result data:', data.result);
          // If result has success: true, return it despite the error
          if (data.result.success === true) {
            console.log('✅ Result indicates success despite error field - returning result');
            return data.result;
          }
        }
        
        const error = new Error(errorMessage);
        // Preserve the error object for better error handling
        error.errorData = data.error;
        throw error;
      }

      // Return result or the full response
      const result = data.result || data;
      if (result?.success === false && result?.error === 'Unauthorized') {
        const authError = new Error(result.message || 'Your session expired. Please login again.');
        authError.isUnauthorized = true;
        authError.httpStatus = 401;
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('attendance-unauthorized', { detail: authError }));
        }
        throw authError;
      }
      console.log('Returning result:', result); // Debug log
      return result;
    } catch (error) {
      console.error('API request error:', error);
      
      // Check if it's a CORS or network error
      if (error.message.includes('Failed to fetch') || error.message.includes('CORS') || error.message.includes('ERR_CERT')) {
        let errorMessage = 'CORS Error: ';
        
        if (error.message.includes('ERR_CERT')) {
          errorMessage += 'SSL certificate error. Please check the server configuration. ';
        } else {
          // CORS error - provide helpful troubleshooting steps
          errorMessage += 'The server is not allowing requests from this origin.\n\n';
          errorMessage += '⚠️ IMPORTANT: If the backend code has been updated to handle CORS:\n';
          errorMessage += '1. ✅ Verify the code changes have been pushed to the server\n';
          errorMessage += '2. ✅ **RESTART THE ODOO SERVER** (this is critical!)\n';
          errorMessage += '3. ✅ Clear browser cache and try again\n';
          errorMessage += '4. ✅ Check that the endpoint URL is correct (no "undefined" in the URL)\n';
          errorMessage += '5. ✅ Verify you\'re using POST method (not GET)\n\n';
          errorMessage += 'Note: The Odoo server MUST be restarted after CORS code changes for headers to work properly.\n';
          errorMessage += `Attempted URL: ${this.baseURL}${endpoint}`;
        }
        
        const networkError = new Error(errorMessage);
        networkError.name = 'CORSError';
        networkError.isCORS = true;
        throw networkError;
      }
      
      throw error;
    }
  }

  // ==================== Authentication ====================

  /**
   * Login to the attendance system
   * @param {string} username - Username or email
   * @param {string} password - Password
   * @returns {Promise<object>} Login response with token
   */
  async login(username, password) {
    return await this.request('/api/attendance/login', { username, password });
  }

  /**
   * Logout from the attendance system
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Logout response
   */
  async logout(token) {
    return await this.request('/api/attendance/logout', {}, token);
  }

  // ==================== Camera Booking API Methods ====================

  /**
   * Camera booking calls return { success, error, message } on failure instead
   * of a JSON-RPC error, so turn those into thrown errors with the server message.
   */
  async cameraRequest(endpoint, params = {}, token) {
    const result = await this.request(endpoint, params, token);
    if (result && result.success === false) {
      const error = new Error(result.message || 'Camera booking request failed');
      error.errorType = result.error || null;
      throw error;
    }
    return result;
  }

  /** Pending + approved bookings in a range. Body: { date_from, date_to }. */
  async getCameraCalendar(params, token) {
    return await this.cameraRequest('/api/camera-booking/calendar', params, token);
  }

  /** Check a time slot. Body: { start, end }. */
  async checkCameraAvailability(params, token) {
    return await this.cameraRequest('/api/camera-booking/availability', params, token);
  }

  /** Bookings with optional { state, date_from, date_to }. Returns pending_count too. */
  async listCameraBookings(params = {}, token) {
    return await this.cameraRequest('/api/camera-booking/list', params, token);
  }

  /** Faculty admins only. Body: { start, end, purpose, location, notes, faculty_id? }. */
  async createCameraBooking(params, token) {
    return await this.cameraRequest('/api/camera-booking/create', params, token);
  }

  async cancelCameraBooking(id, token) {
    return await this.cameraRequest(`/api/camera-booking/${id}/cancel`, {}, token);
  }

  async approveCameraBooking(id, token) {
    return await this.cameraRequest(`/api/camera-booking/${id}/approve`, {}, token);
  }

  async rejectCameraBooking(id, reason, token) {
    return await this.cameraRequest(`/api/camera-booking/${id}/reject`, { reason }, token);
  }

  /**
   * Faculties and admins the caller may book for. Media users get every
   * faculty; faculty admins get only their own.
   */
  async getCameraOptions(token) {
    return await this.cameraRequest('/api/camera-booking/options', {}, token);
  }

  // ==================== Tutor API Methods ====================

  /**
   * Get today's Attendance for the tutor
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Today's Attendance
   */
  async getTodaySessions(token) {
    return await this.request('/api/attendance/today', {}, token);
  }

  /** Get active subject assignments for the logged-in tutor. */
  async getTutorAssignments(token) {
    return await this.request('/api/attendance/assignments', {}, token);
  }

  /**
   * Get calendar Attendance for a specific date or week
   * @param {object} params - Date parameters
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Calendar Attendance
   */
  async getCalendar(params = {}, token) {
    return await this.request('/api/attendance/calendar', params, token);
  }

  /**
   * Get session details
   * @param {string|number} sessionId - Session ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Session details
   */
  async getSessionDetails(sessionId, token) {
    // Validate session ID before making request
    if (!sessionId || sessionId === 'undefined' || sessionId === null) {
      throw new Error('Session ID is required');
    }
    
    const id = parseInt(sessionId);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid session ID');
    }
    
    return await this.request(`/api/attendance/session/${id}`, {}, token);
  }

  /**
   * Mark attendance for a session
   * @param {string|number} sessionId - Session ID
   * @param {array} attendance - Attendance array
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Mark attendance response
   */
  async markAttendance(sessionId, attendance, token) {
    // Validate session ID before making request
    if (!sessionId || sessionId === 'undefined' || sessionId === null) {
      throw new Error('Session ID is required');
    }
    
    const id = parseInt(sessionId);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid session ID');
    }
    
    return await this.request('/api/attendance/mark', {
      session_id: id,
      attendance: attendance
    }, token);
  }

  /**
   * Mark attendance for a session as faculty admin
   * @param {string|number} sessionId - Session ID
   * @param {array} attendance - Attendance array
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Mark attendance response
   */
  async markAttendanceAsFacultyAdmin(sessionId, attendance, token) {
    // Validate session ID before making request
    if (!sessionId || sessionId === 'undefined' || sessionId === null) {
      throw new Error('Session ID is required');
    }
    
    const id = parseInt(sessionId);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid session ID');
    }
    
    // Include override flag to allow Faculty Admin to mark attendance for all sessions,
    // including missed sessions from previous days
    return await this.request('/api/faculty-admin/session/mark', {
      session_id: id,
      attendance: attendance,
      faculty_admin_override: true  // Flag to indicate Faculty Admin override permission
    }, token);
  }

  /**
   * View attendance for a session
   * @param {string|number} sessionId - Session ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Attendance data
   */
  async viewAttendance(sessionId, token) {
    // Validate session ID before making request
    if (!sessionId || sessionId === 'undefined' || sessionId === null) {
      throw new Error('Session ID is required');
    }
    
    const id = parseInt(sessionId);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid session ID');
    }
    
    return await this.request(`/api/attendance/view/${id}`, {}, token);
  }

  /**
   * Get attendance history that why
   * @param {object} params - Filter parameters (from_date, to_date, limit)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Attendance history
   */
  async getHistory(params = {}, token) {
    return await this.request('/api/attendance/history', params, token);
  }

  /**
   * Get previous period's attendance for the same subject/tutor/batch on the same day
   * Used to auto-load attendance from earlier periods when taking attendance for consecutive periods
   * @param {object} params - Session parameters (session_id, subject_id, tutor_id, batch_ids, session_date, time_start)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Previous attendance data
   */
  async getPreviousAttendance(params, token) {
    return await this.request('/api/attendance/previous', params, token);
  }

  // ==================== Faculty Admin API Methods ====================

  /**
   * Get faculty admin dashboard data
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Dashboard data
   */
  async getFacultyDashboard(token) {
    return await this.request('/api/faculty-admin/dashboard', this.withDefaultFacultyScope({}), token);
  }

  /**
   * Get active timetable term configuration
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Active term config with auto-populated dates
   */
  async getActiveTermConfig(token) {
    return await this.request('/api/faculty-admin/active-term-config', this.withDefaultFacultyScope({}), token);
  }

  /**
   * Get list of timetables
   * @param {object} params - Filter parameters
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Timetables list
   */
  async getTimetables(params = {}, token) {
    return await this.request('/api/faculty-admin/timetables', this.withDefaultFacultyScope(params), token);
  }

  /**
   * Get a specific timetable
   * @param {string|number} timetableId - Timetable ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Timetable details
   */
  async getTimetable(timetableId, token) {
    // Validate timetable ID before making request
    if (!timetableId || timetableId === 'undefined' || timetableId === null) {
      throw new Error('Timetable ID is required');
    }
    
    const id = parseInt(timetableId);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid timetable ID');
    }
    
    return await this.request(`/api/faculty-admin/timetable/${id}`, {}, token);
  }

  /**
   * Create a new timetable
   * @param {object} data - Timetable data
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Create response
   */
  async createTimetable(data, token) {
    return await this.request(
      '/api/faculty-admin/timetable/create',
      this.withDefaultFacultyScope(data),
      token
    );
  }

  /**
   * Update an existing timetable
   * Uses the bearer-token faculty-admin JSON-RPC endpoint
   * @param {string|number} timetableId - Timetable ID
   * @param {object} data - Updated timetable data
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Update response
   */
  async updateTimetable(timetableId, data, token) {
    if (!timetableId || timetableId === 'undefined' || timetableId === null) {
      throw new Error('Timetable ID is required');
    }

    const id = parseInt(timetableId);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid timetable ID');
    }

    // Faculty admins authenticate with the portal bearer token, not an Odoo
    // browser session. Always use the token-aware faculty-admin endpoint.
    return await this.request(
      `/api/faculty-admin/timetable/${id}/update`,
      this.withDefaultFacultyScope(data),
      token
    );
  }

  /**
   * Delete a timetable
   * Uses DELETE /api/timetable/<id> endpoint
   * First tries simple JSON format, falls back to JSON-RPC 2.0 if needed
   * @param {string|number} timetableId - Timetable ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Delete response
   */
  async deleteTimetable(timetableId, token) {
    // Validate timetable ID before making request
    if (!timetableId || timetableId === 'undefined' || timetableId === null) {
      throw new Error('Timetable ID is required');
    }
    
    const id = parseInt(timetableId);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid timetable ID');
    }
    
    // Try simple JSON format first (as per API documentation)
    try {
      const result = await this.simpleRequest(`/api/timetable/${id}`, {}, token, 'DELETE');
      return result;
    } catch (error) {
      // Check if it's a network/404 error - if so, try the faculty-admin endpoint
      const isNetworkError = error.message.includes('Network error') || 
                            error.message.includes('404') || 
                            error.message.includes('endpoint not found') ||
                            error.message.includes('Failed to fetch');
      
      if (isNetworkError) {
        // If that fails, try with faculty-admin endpoint using JSON-RPC 2.0 (like other timetable methods)
        console.warn('Simple JSON format failed, trying JSON-RPC 2.0 format with faculty-admin endpoint');
        try {
          return await this.request(`/api/faculty-admin/timetable/${id}/delete`, {}, token);
        } catch (fallbackError) {
          // If both fail, throw the original error with more context
          throw new Error(`Failed to delete timetable. Tried both /api/timetable/${id} and /api/faculty-admin/timetable/${id}/delete. Original error: ${error.message}`);
        }
      } else {
        // If it's not a network error, re-throw it (might be validation error, etc.)
        throw error;
      }
    }
  }

  /**
   * Get list of batches
   * @param {object} params - Filter parameters (department_id)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Batches list
   */
  async getBatches(params = {}, token) {
    return await this.request('/api/faculty-admin/batches', this.withDefaultFacultyScope(params), token);
  }

  /** Get the configured teaching campuses. */
  async getCampuses(token) {
    return await this.request('/api/faculty-admin/campuses', {}, token);
  }

  /**
   * Get list of faculties assigned to the faculty admin
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Faculties list
   */
  async getFaculties(token) {
    return await this.request('/api/faculty-admin/faculties', {}, token);
  }

  /**
   * Get list of departments
   * @param {object} params - Filter parameters (faculty_id)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Departments list
   */
  async getDepartments(params = {}, token) {
    return await this.request('/api/faculty-admin/departments', this.withDefaultFacultyScope(params), token);
  }

  /**
   * Get list of subjects
   * @param {object} params - Filter parameters (batch_ids, semester)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Subjects list
   */
  async getSubjects(params = {}, token) {
    return await this.request('/api/faculty-admin/subjects', this.withDefaultFacultyScope(params), token);
  }

  /**
   * Auto-populate subjects for batches and semester
   * @param {array} batchIds - Array of batch IDs
   * @param {number} semester - Semester number
   * @param {number} shiftId - Shift ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Subjects list
   */
  async autoPopulateSubjects(batchIds, semester, shiftId, campus, facultyId, token) {
    return await this.request('/api/faculty-admin/auto-populate-subjects', {
      ...this.withDefaultFacultyScope(facultyId ? { faculty_id: Number(facultyId) } : {}),
      batch_ids: batchIds,
      semester: semester,
      shift_id: shiftId,
      campus: campus
    }, token);
  }

  /**
   * Get list of Attendance (faculty admin view)
   * @param {object} params - Filter parameters
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Attendance list
   */
  async getSessions(params = {}, token) {
    return await this.request('/api/faculty-admin/sessions', this.withDefaultFacultyScope(params), token);
  }

  async getBatchStudents(batchId, token, campus = '') {
    const params = { batch_id: Number(batchId) };
    if (campus) params.campus = campus;
    return await this.request('/api/faculty-admin/batch-students', this.withDefaultFacultyScope(params), token);
  }

  /**
   * Get list of Attendance for faculty admin (alias for getSessions)
   * @param {object} params - Filter parameters
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Attendance list
   */
  async getFacultySessions(params = {}, token) {
    return await this.getSessions(params, token);
  }

  /**
   * Get a specific session (faculty admin view)
   * @param {string|number} sessionId - Session ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Session details
   */
  async getSession(sessionId, token) {
    // Validate session ID before making request
    if (!sessionId || sessionId === 'undefined' || sessionId === null) {
      throw new Error('Session ID is required');
    }
    
    const id = parseInt(sessionId);
    if (isNaN(id) || id <= 0) {
      throw new Error('Invalid session ID');
    }
    
    // Uses POST method via this.request() - matches backend route which accepts POST
    // Backend route: @http.route(..., methods=['POST', 'OPTIONS'], type='http', csrf=False)
    // Backend manually sets CORS headers in Response (no cors='*' to avoid duplicates)
    // This.request() sends POST with JSON-RPC 2.0 format
    return await this.request(`/api/faculty-admin/session/${id}`, {}, token);
  }

  /**
   * Get a specific session (faculty admin view) - alias for getSession
   * @param {string|number} sessionId - Session ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Session details
   */
  async getFacultySession(sessionId, token) {
    return await this.getSession(sessionId, token);
  }

  /**
   * Get attendance statistics
   * @param {object} params - Filter parameters
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Attendance statistics
   */
  async getAttendanceStats(params = {}, token) {
    return await this.request('/api/faculty-admin/attendance-stats', this.withDefaultFacultyScope(params), token);
  }

  // ==================== Helper Methods ====================

  /**
   * Get periods for a shift
   * @param {number} shiftId - Shift ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Periods list
   */
  async getPeriods(shiftId, token) {
    return await this.request('/api/faculty-admin/periods', { shift_id: shiftId }, token);
  }

  /**
   * Get weekdays list
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Weekdays list
   */
  async getWeekdays(token) {
    return await this.request('/api/faculty-admin/weekdays', {}, token);
  }

  /**
   * Get academic years list
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Academic years list
   */
  async getAcademicYears(token) {
    return await this.request('/api/faculty-admin/academic-years', {}, token);
  }

  /**
   * Get shifts list
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Shifts list
   */
  async getShifts(token) {
    return await this.request('/api/faculty-admin/shifts', {}, token);
  }

  /**
   * Health check / ping endpoint
   * @param {string} token - Bearer token (optional for ping)
   * @returns {Promise<object>} Ping response
   */
  async ping(token = null) {
    return await this.request('/api/attendance/ping', {}, token);
  }

  // ==================== Tutor Portal API Methods ====================
  // Note: Tutor Portal API uses simple JSON format (not JSON-RPC 2.0)

  /**
   * Make a request to Tutor Portal API or Timetable API (uses simple JSON format)
   * @param {string} endpoint - API endpoint
   * @param {object} params - Request parameters
   * @param {string} token - Bearer token for authentication
   * @param {string} method - HTTP method (GET, POST, PUT, DELETE, PATCH) - defaults to POST
   * @returns {Promise<object>} API response (extracted from result field)
   */
  async simpleRequest(endpoint, params = {}, token = null, method = 'POST') {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token is provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      // Ensure params is an object
      if (!params || typeof params !== 'object') {
        console.error('Invalid params:', params);
        throw new Error('Request parameters must be an object');
      }
      
      // Check if this is the change-password endpoint - it might need JSON-RPC 2.0 format
      // Based on the backend response format (JSON-RPC 2.0), the request might also need to be JSON-RPC 2.0
      const needsJsonRpc = endpoint.includes('change-password');
      
      let requestBody;
      if (needsJsonRpc) {
        // Use JSON-RPC 2.0 format for change-password endpoint
        requestBody = JSON.stringify({
          jsonrpc: '2.0',
          method: 'call',
          params: params
        });
        console.log(`API Request to: ${this.baseURL}${endpoint} (using JSON-RPC 2.0 format, method: ${method})`);
      } else {
        // Use simple JSON format for other endpoints
        requestBody = JSON.stringify(params);
        console.log(`API Request to: ${this.baseURL}${endpoint} (using simple JSON format, method: ${method})`);
      }
      
      console.log('Request params object:', params);
      console.log('Request params keys:', Object.keys(params || {}));
      console.log('Request params values check:', {
        current_password: params.current_password ? `*** (length: ${params.current_password.length})` : 'MISSING',
        new_password: params.new_password ? `*** (length: ${params.new_password.length})` : 'MISSING',
        allKeys: Object.keys(params)
      });
      console.log('Request body string:', requestBody);
      console.log('Request body length:', requestBody.length);
      try {
        console.log('Request body parsed:', JSON.parse(requestBody));
      } catch (e) {
        console.error('Failed to parse request body:', e);
      }
      console.log('Has token:', !!token);
      
      // For DELETE requests with empty body, don't send body
      const fetchOptions = {
        method: method,
        headers: headers,
      };
      
      // Only include body for methods that support it and when there's data
      if (method !== 'GET' && method !== 'DELETE' && Object.keys(params).length > 0) {
        fetchOptions.body = requestBody;
      } else if (method === 'PUT' || method === 'PATCH') {
        // PUT and PATCH should have a body even if empty
        fetchOptions.body = requestBody;
      }
      
      const response = await fetch(`${this.baseURL}${endpoint}`, fetchOptions);

      console.log(`Tutor Portal API Response status: ${response.status}`, response.statusText);

      // Check content type to detect HTML responses (404 pages)
      const contentType = response.headers.get('content-type') || '';
      const isHTML = contentType.includes('text/html');

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Tutor Portal API Error Response:', errorText);
        
        if (isHTML || response.status === 404) {
          throw new Error(
            `API endpoint not found (404). The backend route ${endpoint} may not be implemented yet. ` +
            `Please ensure the Tutor Portal API is properly configured on the backend.`
          );
        }
        
        // Try to parse as JSON for structured error messages
        try {
          const errorJson = JSON.parse(errorText);
          // Handle JSON-RPC error format
          if (errorJson.result && errorJson.result.message) {
            throw new Error(errorJson.result.message);
          }
          if (errorJson.message) {
            throw new Error(errorJson.message);
          }
        } catch (e) {
          // Not JSON, use the text as-is
        }
        
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      // If response is HTML, it's likely a routing issue
      if (isHTML) {
        const errorText = await response.text();
        throw new Error(
          `Received HTML response instead of JSON. The endpoint ${endpoint} may not be properly configured. ` +
          `Please check that the Tutor Portal API routes are registered on the backend.`
        );
      }

      const data = await response.json();
      console.log('API Response data:', data);
      console.log('API Response data (stringified):', JSON.stringify(data, null, 2));

      // Extract result from JSON-RPC 2.0 format or use direct response
      // Response format options:
      // 1. JSON-RPC 2.0: { jsonrpc: "2.0", id: null, result: { success: true, ... } }
      // 2. Simple JSON: { success: true, data: {...}, message: "..." }
      const result = data.result || data;
      console.log('Extracted result:', result);

      // Check for error in response
      if (result.success === false) {
        console.error('Backend returned error:', {
          success: result.success,
          error: result.error,
          message: result.message
        });
        throw new Error(result.message || result.error || 'API request failed');
      }

      // Return the result object (which contains the actual API data)
      return result;
    } catch (error) {
      console.error('Tutor Portal API request error:', error);
      
      // Provide more helpful error messages
      if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
        throw new Error(
          'Network error: Unable to connect to the API. Please check:\n' +
          '1. The backend server is running\n' +
          '2. CORS is properly configured\n' +
          '3. The API endpoint is correct'
        );
      }
      
      throw error;
    }
  }

  /**
   * Get list of tutors with optional filters
   * @param {object} params - Filter parameters (show_only_without_users, show_only_with_subjects, limit)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Tutors list
   */
  async getTutorsList(params = {}, token) {
    return await this.simpleRequest('/api/tutor-portal/tutors/list', params, token);
  }

  /**
   * Get detailed tutor information
   * @param {number} tutorId - Tutor ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Tutor details
   */
  async getTutorDetails(tutorId, token) {
    if (!tutorId) {
      throw new Error('Tutor ID is required');
    }
    return await this.simpleRequest(`/api/tutor-portal/tutors/${tutorId}`, {}, token);
  }

  /**
   * Check portal access status for a tutor
   * @param {number} tutorId - Tutor ID
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Portal status
   */
  async getTutorPortalStatus(tutorId, token) {
    if (!tutorId) {
      throw new Error('Tutor ID is required');
    }
    return await this.simpleRequest(`/api/tutor-portal/tutors/${tutorId}/portal-status`, {}, token);
  }

  /**
   * Create portal user for a tutor
   * @param {number} tutorId - Tutor ID
   * @param {object} options - Options (auto_generate_password, send_credentials_email)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Create response
   */
  async createTutorPortalUser(tutorId, options = {}, token) {
    if (!tutorId) {
      throw new Error('Tutor ID is required');
    }
    return await this.simpleRequest(
      `/api/tutor-portal/tutors/${tutorId}/create-portal-user`,
      {
        auto_generate_password: options.auto_generate_password !== false,
        send_credentials_email: options.send_credentials_email === true
      },
      token
    );
  }

  /**
   * Get tutor portal statistics
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Statistics
   */
  async getTutorPortalStats(token) {
    return await this.simpleRequest('/api/tutor-portal/stats', {}, token);
  }

  /**
   * Get portal setup wizard records (portal user setup records)
   * @param {object} params - Filter parameters (limit, offset)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Wizard records list
   */
  async getPortalSetupRecords(params = {}, token) {
    // Try the wizard records endpoint
    return await this.simpleRequest('/api/tutor-portal/wizard-records', params, token);
  }

  /**
   * Update portal user password
   * @param {object} passwordData - Password data (current_password, new_password)
   * @param {string} token - Bearer token
   * @returns {Promise<object>} Update response
   */
  async updatePassword(passwordData, token) {
    // Don't trim passwords - send them exactly as provided
    // The backend should handle password comparison as-is
    const currentPassword = passwordData.current_password || '';
    const newPassword = passwordData.new_password || '';
    
    if (!currentPassword || !newPassword) {
      throw new Error('Current password and new password are required');
    }
    
    console.log('updatePassword API call:', {
      endpoint: '/api/tutor-portal/change-password',
      hasCurrentPassword: !!currentPassword,
      currentPasswordLength: currentPassword.length,
      currentPasswordPreview: currentPassword ? currentPassword.substring(0, 2) + '***' : 'empty',
      hasNewPassword: !!newPassword,
      newPasswordLength: newPassword.length,
      newPasswordPreview: newPassword ? newPassword.substring(0, 2) + '***' : 'empty',
      passwordDataReceived: {
        hasCurrentPassword: !!passwordData.current_password,
        currentPasswordLength: passwordData.current_password?.length || 0,
        hasNewPassword: !!passwordData.new_password,
        newPasswordLength: passwordData.new_password?.length || 0
      }
    });
    
    // Try JSON-RPC 2.0 format first (backend might expect this)
    // If that doesn't work, we'll fall back to simple JSON
    const requestParams = {
      current_password: currentPassword,
      new_password: newPassword
    };
    
    console.log('Request params being sent to simpleRequest:', {
      keys: Object.keys(requestParams),
      current_password_exists: 'current_password' in requestParams,
      new_password_exists: 'new_password' in requestParams,
      current_password_value: requestParams.current_password ? '***' : 'empty',
      new_password_value: requestParams.new_password ? '***' : 'empty',
      current_password_length: requestParams.current_password?.length || 0,
      new_password_length: requestParams.new_password?.length || 0
    });
    
    // Use simpleRequest which sends simple JSON format
    // The backend should accept this based on the API documentation
    return await this.simpleRequest(
      '/api/tutor-portal/change-password',
      requestParams,
      token
    );
  }
}

// Export a singleton instance
const attendanceAPI = new AttendanceAPI();
export default attendanceAPI;
